import { Button, Flex, Text } from "@chakra-ui/react";
import {
  catchErrorHandler,
  getLocalStorageData,
  storeLocalStorageData,
} from "@picovico/utils";

import { getConfigSSR } from "@/api/config";
import { getPageRoutes } from "@/routes/pageRoutes";
import {
  isSocialAuthStateValid,
  takeSocialAuthState,
} from "utils/socialAuthState";
import NextLink from "next/link";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import { useAppDispatch } from "store";
import { useLazySocialLoginVerifyQuery } from "store/services/auth";
import { resetUserStateAction } from "store/slices";

const GENERIC_ERROR =
  "We could not sign you in with this account. Please try again.";

function SocialVerifyPage() {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const [verify] = useLazySocialLoginVerifyQuery();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // A provider code is single-use and the effect below re-runs whenever the router object
  // changes, so remember which code has been sent: a second send can only fail.
  const handledCode = useRef<string | null>(null);

  useEffect(() => {
    if (!router.isReady) return;

    if (getLocalStorageData("token")) return;

    const code = router.query.code as string | undefined;
    if (!code) {
      // The customer declined at the provider (`?error=access_denied`) or came here directly.
      setErrorMessage(GENERIC_ERROR);
      return;
    }
    if (handledCode.current === code) return;
    handledCode.current = code;

    // The state kept when this browser started the sign-in must come back with the code; a
    // callback link made from someone else's sign-in carries theirs (or none) and is refused.
    const returnedState = router.query.state as string | undefined;
    if (!isSocialAuthStateValid(returnedState, takeSocialAuthState())) {
      setErrorMessage(
        "This sign-in link is not valid for this browser. Please start again.",
      );
      return;
    }

    const params = { ...router.query } as Record<string, string | string[]>;
    const providerName = (router.query["social-auth-provider"] as string) || "";

    verify({ idx: providerName, params })
      .unwrap()
      .then((res) => {
        if (res?.data?.token) {
          storeLocalStorageData("token", res.data.token);
          if (res?.data?.device_id) {
            storeLocalStorageData("device_id", res.data.device_id);
          }
        }

        dispatch(resetUserStateAction());

        const nextUrl = (router.query.next as string) || null;
        if (nextUrl) {
          router.replace(getPageRoutes(nextUrl));
        } else {
          router.replace(getPageRoutes("home"));
        }
      })
      .catch((error) => {
        // Stop here. Sending the customer straight back to /login restarts the provider round
        // trip silently, and a refusal that is not going to change turns into a request loop.
        catchErrorHandler(error, setErrorMessage);
      });
  }, [router.isReady, router.query, verify, dispatch, router]);

  if (!errorMessage) return null;

  const loginHref = router.query.next
    ? `${getPageRoutes("login")}?next=${encodeURIComponent(router.query.next as string)}`
    : getPageRoutes("login");

  return (
    <Flex
      direction="column"
      align="center"
      gap={4}
      py={20}
      px={4}
      textAlign="center"
    >
      <Text fontWeight={500} color="danger.700">
        {errorMessage}
      </Text>
      <Button as={NextLink} href={loginHref}>
        Back to login
      </Button>
    </Flex>
  );
}

export const getServerSideProps = getConfigSSR;

export default SocialVerifyPage;
